// rolesApi — Custom RBAC role CRUD client (P3 API surface).
// Spec: docs/specs/custom-rbac.md §6.1.

import { apiFetch } from "../../lib/api-client.js";

export interface RoleRow {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  is_system: boolean;
  cloned_from: string | null;
  created_at: number;
  created_by_user_id: string | null;
  updated_at: number;
  updated_by_user_id: string | null;
  archived_at: number | null;
  permission_count: number;
  assigned_user_count: number;
}

export interface RoleDetail extends Omit<RoleRow, "permission_count" | "assigned_user_count"> {
  permissions: string[];
  assigned_user_count: number;
}

export interface RoleTemplate {
  id: string;
  name: string;
  description: string | null;
  industry_tag: string;
  suggested_permissions: string[];
  display_order: number;
  is_active: boolean;
}

export class RolesApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, public readonly body: unknown) {
    super(`${status} ${code}`);
  }
}

async function asError(r: Response): Promise<RolesApiError> {
  const body = await r.json().catch(() => ({})) as { error?: string; code?: string };
  return new RolesApiError(r.status, body.error ?? body.code ?? `http_${r.status}`, body);
}

export function friendlyRolesError(err: unknown): string {
  if (!(err instanceof RolesApiError)) return err instanceof Error ? err.message : "未知錯誤";
  const body = (err.body ?? {}) as Record<string, unknown>;
  switch (err.code) {
    case "duplicate_name":
      return "角色名稱已存在，請改用其他名稱";
    case "system_role_immutable":
      return "系統預設角色不可修改 — 請先複製為自訂角色再編輯";
    case "wrong_tenant":
      return "此角色屬於其他單位，無法修改";
    case "role_archived":
      return "此角色已封存，無法繼續修改";
    case "role_in_use":
      return `此角色目前指派給 ${body.assigned_user_count ?? 0} 位使用者，請先解除指派再刪除`;
    case "cannot_grant_unheld_permissions":
      return `您無法授予自己沒有的權限：${(body.missing as string[] ?? []).join("、")}`;
    case "unknown_permissions":
      return `包含未知權限 id：${(body.unknown as string[] ?? []).join("、")}`;
    case "name_too_long":
      return "角色名稱過長（上限 120 字）";
    case "cannot_remove_last_tenant_admin":
      return "此使用者是單位最後一位「單位管理員」，請先指派此角色給其他人";
    case "unknown_industry_tag":
      return "未支援的行業標籤";
    case "not_found":
      return "找不到該資料";
    default:
      return err.message;
  }
}

export async function listRoles(tenantId?: string | null): Promise<{ items: RoleRow[] }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function getRole(id: string, tenantId?: string | null): Promise<RoleDetail> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles/${encodeURIComponent(id)}${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export interface CreateRoleInput {
  name: string;
  description?: string | null;
  permission_ids: string[];
  cloned_from?: string | null;
}

export async function createRole(input: CreateRoleInput, tenantId?: string | null): Promise<{ ok: true; id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles${qs}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export interface UpdateRoleInput {
  name?: string;
  description?: string | null;
}

export async function updateRole(id: string, input: UpdateRoleInput, tenantId?: string | null): Promise<{ ok: true }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function replaceRolePermissions(id: string, permissionIds: string[], tenantId?: string | null): Promise<{ ok: true; added: number; removed: number }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles/${encodeURIComponent(id)}/permissions${qs}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ permission_ids: permissionIds }),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function deleteRole(id: string, tenantId?: string | null): Promise<{ ok: true }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/roles/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function listRoleTemplates(industryTag?: string): Promise<{ items: RoleTemplate[] }> {
  const qs = industryTag ? `?industry_tag=${encodeURIComponent(industryTag)}` : "";
  const r = await apiFetch(`/v1/platform/role-templates${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function getRoleTemplate(id: string): Promise<RoleTemplate> {
  const r = await apiFetch(`/v1/platform/role-templates/${encodeURIComponent(id)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export interface CreateRoleTemplateInput {
  name: string;
  description?: string | null;
  industry_tag: string;
  suggested_permissions: string[];
  display_order?: number;
}

export async function createRoleTemplate(input: CreateRoleTemplateInput): Promise<{ ok: true; id: string }> {
  const r = await apiFetch(`/v1/platform/role-templates`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export interface UpdateRoleTemplateInput {
  name?: string;
  description?: string | null;
  industry_tag?: string;
  suggested_permissions?: string[];
  display_order?: number;
  is_active?: boolean;
}

export async function updateRoleTemplate(id: string, input: UpdateRoleTemplateInput): Promise<{ ok: true }> {
  const r = await apiFetch(`/v1/platform/role-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export async function deleteRoleTemplate(id: string): Promise<{ ok: true }> {
  const r = await apiFetch(`/v1/platform/role-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}

// User-role assignment (P4)

export interface UserRoleAssignment {
  id: string;
  role_id: string;
  role_name: string;
  role_is_system: boolean;
  scope_json: { sites?: string[]; buildings?: string[]; clients?: string[] } | null;
  assigned_at: number;
  assigned_by_user_id: string;
}

export async function getUserRoles(userId: string, tenantId?: string | null): Promise<{ items: UserRoleAssignment[] }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/roles${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return r.json();
}

export interface AssignmentInput {
  role_id: string;
  scope_json?: { sites?: string[]; buildings?: string[]; clients?: string[] } | null;
}

export async function putUserRoles(userId: string, assignments: AssignmentInput[], tenantId?: string | null): Promise<{ ok: true; revoked: number; inserted: number; rescoped: number }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/roles${qs}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ assignments }),
  });
  if (!r.ok) throw await asError(r);
  return r.json();
}
